import { useState } from "react";

/**
 * <Avatar /> — uniform customer/contact avatar with graceful fallback.
 *
 * Renders the WhatsApp profile picture when available, otherwise the
 * first letter of the contact name on a slate-900 disc. We treat broken
 * image URLs the same as "no avatar" so a stale CDN link never leaves
 * the UI with an empty square — the image silently swaps to the
 * initial avatar instead.
 *
 * Sizes are tokens so callers don't have to remember Tailwind classes:
 *   xs ≈ list dense, sm ≈ list default, md ≈ chat header, lg ≈ profile.
 */
const SIZES = {
  xs: { box: "w-7 h-7", text: "text-[11px]" },
  sm: { box: "w-9 h-9", text: "text-[13px]" },
  md: { box: "w-10 h-10", text: "text-sm" },
  lg: { box: "w-12 h-12", text: "text-base" },
  xl: { box: "w-16 h-16", text: "text-lg" },
};

export default function Avatar({ name, url, size = "md", className = "", dim = false, testid }) {
  const [broken, setBroken] = useState(false);
  const cfg = SIZES[size] || SIZES.md;
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  const hasImg = !!url && !broken;

  if (hasImg) {
    return (
      <div
        className={`${cfg.box} rounded-full overflow-hidden shrink-0 bg-slate-100 ${className}`}
        data-testid={testid}
      >
        <img
          src={url}
          alt={name || "avatar"}
          loading="lazy"
          onError={() => setBroken(true)}
          className={`w-full h-full object-cover ${dim ? "opacity-60" : ""}`}
        />
      </div>
    );
  }

  return (
    <div
      className={`${cfg.box} rounded-full flex items-center justify-center font-semibold shrink-0 ${
        dim ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white"
      } ${className}`}
      data-testid={testid}
    >
      <span className={cfg.text}>{initial}</span>
    </div>
  );
}
